import { ListViewGridColumnGeneratorParams } from '../utils/VizPortalTypes';
import { TypedColumn } from './TypedDataGridInterfaces';

/**
 * The "params" are various configuration parameters that might be required to define the columns
 * correctly.
 *
 * Columns are defined as functions so they can be re-generated at each render, in order for any
 * localized text (or other runtime-dependent properties) to be up-to-date.
 */
export type ColumnFactory<TParams extends Record<string, unknown>, TRowData, OrderFields extends string = ''> =
  (params: TParams) => TypedColumn<TRowData, OrderFields>;

export type ConvertColumnParams<DC3ColumnParams> =
  (vizPortalColumnParams: ListViewGridColumnGeneratorParams) => DC3ColumnParams;

/**
 * For grids that are also used in VizPortal, we put the columns through this function to create
 * a wrapped version of the column factories that take in VizPortal's column params.
 */
// tslint:disable-next-line: max-line-length
export function mapDC3ColumnFactoriesToVizPortalColumnFactories<TParams extends Record<string, unknown>, TRowData, OrderFields extends string>(
  dc3ColumnFactories: ReadonlyArray<ColumnFactory<TParams, TRowData, OrderFields>>,
  convertColumnParams: ConvertColumnParams<TParams>
): ReadonlyArray<ColumnFactory<ListViewGridColumnGeneratorParams, TRowData, OrderFields>> {

  return dc3ColumnFactories.map(dc3ColumnFactory => {
    return (vizPortalColumnParams: ListViewGridColumnGeneratorParams) => {
      const dc3ColumnParams = convertColumnParams(vizPortalColumnParams);
      const column = dc3ColumnFactory(dc3ColumnParams);

      // When the column exists in VizPortal Client, change the data keys to lowercase so they will match the
      // lowercase sort fields that flow through VizPortal's system. This allows the data grid to match the column's
      // data key with the sort field name coming to the data grid props (via VPC's redux store) and give the correct
      // ARIA sorting indications on the column header.
      const dataKey = column.dataKey.toLowerCase();

      return {
        ...column,
        dataKey,
      } as TypedColumn<TRowData, OrderFields>;
    };
  });
}
